import { useDispatch, useSelector } from "react-redux";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActionApprovalLogType,
  ActionStatus,
  ActionSubStatus,
  ActionType,
  IActionsResponse,
  PolicyProcessType,
  PolicyState,
  StatementType,
  actionConfig,
  actionNotesReadable,
  actionStatementTypes,
  completeLikeActionStatuses,
  currencyFormatter,
  emailTypes,
  langauge,
  toDecimal,
} from "shared";
import {  When } from "react-if";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import {
  BanknotesIcon,
  BoltIcon,
  ChatBubbleLeftEllipsisIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  ReceiptPercentIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { Tooltip } from "@mui/material";
import { DateTime } from "luxon";

import { Drawer } from "../../../common/Atoms/Drawer";
import { RootState } from "../../store";
import { closeAndClear } from "../../slices/actionDrawer";
import { DescriptionItem } from "../../../common/Atoms/DescriptionList";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { WrappedLink } from "../WrappedLink";
import { Button, ButtonColorOpts } from "../../../common/Atoms/Button";
import { useActionInvoiceMutation } from "../../services/api/invoiceApi/invoice";
import { useActions, useDashboardActions } from "../../hooks/useActions";
import {
  useActionNowMutation,
  useRetryActionMutation,
  useRemoveActionMutation,
  useRemoveFromBatchMutation,
  useRevertActionMutation,
  useRegenerateChildActionsMutation,
  useApproveActionsMutation,
  useRevertApprovalsMutation,
  useSkipActionsMutation,
  useGetActionByIdQuery,
} from "../../services/api/actionApi/action";
import { Badge } from "../../../common/Atoms/Badge";
import { useApiRequest } from "../../hooks/useApiRequest";
import { env } from "../../../common/lib/env";
import { useGroups } from "../../hooks/useGroups";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { DatePopover } from "../../../common/Components/DatePopover";
import { Notification } from "../../../common/Atoms/Notification";

import { ActionNowModal } from "./ActionNowModal";
import { ActionStatusBadge } from "./ActionStatusBadge";
import { IgnoreLateFeeModal } from "./IgnoreLateFeeModal";
import { ActionPreviewSection } from "./ActionPreviewSection";
import {
  DeliveryTimeline,
  DetailGrid,
  EmailCard,
  SectionLabel,
} from "./ActionDrawerSections";

const typeIcons: Partial<Record<ActionType, typeof EnvelopeIcon>> = {
  [ActionType.LATE_FEE]: BanknotesIcon,
  [ActionType.ISSUE_STATEMENT_LATE_FEE]: BanknotesIcon,
  [ActionType.DISCOUNT_APPLY]: ReceiptPercentIcon,
  [ActionType.DISCOUNT_REMOVE]: ReceiptPercentIcon,
  [ActionType.SEND_SMS]: ChatBubbleLeftEllipsisIcon,
  [ActionType.ESCALATION]: PaperAirplaneIcon,
  [ActionType.SEND_STATEMENT]: DocumentTextIcon,
};

const eyebrow: Record<ActionStatus, string> = {
  [ActionStatus.PENDING]: `Scheduled action`,
  [ActionStatus.APPROVAL_PENDING]: `Scheduled action`,
  [ActionStatus.COMPLETE]: `Completed action`,
  [ActionStatus.FAILED]: `Failed action`,
  [ActionStatus.SUSPENDED]: `Suspended action`,
  [ActionStatus.REVERTED]: `Reverted action`,
  [ActionStatus.SKIPPED]: `Skipped action`,
};

const hasOriginalInvoice = [
  ActionType.LATE_FEE,
  ActionType.DISCOUNT_APPLIED_EMAIL,
  ActionType.DISCOUNT_REMOVED_EMAIL,
  ActionType.DISCOUNT_APPLY,
  ActionType.DISCOUNT_REMOVE,
  ActionType.LATE_FEE_EMAIL,
  ActionType.REMINDER,
  ActionType.SEND_SMS,
  ActionType.ESCALATION,
  ActionType.PAYMENT_PLAN_REMINDER,
];

type DrawerAction = IActionsResponse[`actions`][number] & {
  onDemandType?: `email` | `sms` | `statement`;
};

function isEmailLikeAction(action: { type: ActionType; onDemandType?: string | null }) {
  return emailTypes.includes(action.type)
    || (
      action.type === ActionType.ON_DEMAND
      && (action.onDemandType === `email` || action.onDemandType === `statement`)
    );
}

export function ActionDrawer() {
  const drawerProps = useSelector((s: RootState) => s.actionDrawer);
  const dispatch = useDispatch();
  const currentOrg = useGetSelectedOrganisation();
  const [actionInvoice, { isLoading: actionInvoiceLoading }] = useActionInvoiceMutation();
  const [revertAction, { isLoading: revertActionLoading }] = useRevertActionMutation();
  const [removeAction, { isLoading: removeActionLoading }] = useRemoveActionMutation();
  const [removeFromBatch, { isLoading: removeFromBatchLoading }] = useRemoveFromBatchMutation();
  const [regenerateChildActions, { isLoading: regenerateChildActionsLoading }] = useRegenerateChildActionsMutation();
  const [approveActions, { isLoading: approveActionsLoading }] = useApproveActionsMutation();
  const [revertApprovals, { isLoading: revertApprovalsLoading }] = useRevertApprovalsMutation();
  const [skipActions, { isLoading: skipActionsLoading }] = useSkipActionsMutation();
  const [actionNow, { isLoading: actionNowLoading }] = useActionNowMutation();
  const [retryAction, { isLoading: retryActionLoading }] = useRetryActionMutation();
  const [showActionNowModal, setShowActionNowModal] = useState(false);
  const [showIgnoreFeeModal, setShowIgnoreFeeModal] = useState(false);
  const [downloadingStatement, setDownloadingStatement] = useState(false);
  const { data: actionsData } = useActions();
  const request = useApiRequest();
  const { data: groups } = useGroups();

  const normalAction = useMemo(() => {
    return actionsData?.actions.find(a => a.id === drawerProps.actionId);
  }, [actionsData, drawerProps.actionId]);

  const { data: dashActions } = useDashboardActions(!!normalAction); // Skip if we have an action

  const dashAction = useMemo(() => {
    return dashActions?.actions.find(a => a.id === drawerProps.actionId);
  }, [dashActions, drawerProps.actionId]);

  const shouldFetchActionById = !!drawerProps.actionId && !normalAction && !dashAction;
  const {
    data: fallbackAction,
    isLoading: isLoadingFallbackAction,
    isFetching: isFetchingFallbackAction,
    isUninitialized: isFallbackActionUninitialized,
  } = useGetActionByIdQuery({
    organisationId: currentOrg?.id || ``,
    actionId: drawerProps.actionId || ``,
  }, {
    skip: !currentOrg?.id || !shouldFetchActionById,
  });

  const action = (normalAction || dashAction || fallbackAction) as DrawerAction | undefined;

  const contactGroup = useMemo(() => {
    if (!action) return null;

    if (!action.contact?.groupId) return null;

    return groups?.find(g => g.id === action.contact.groupId);
  }, [action, groups]);

  const formatter = currencyFormatter(action?.invoice?.xero_currency);

  // CHECK IF WE SHOULD CLOSE THE DRAWER AS THE ACTION NO LONGER EXISTS IN CACHE
  useEffect(() => {
    if (!drawerProps.open || !drawerProps.actionId) {
      return;
    }

    if (!action && !isLoadingFallbackAction && !isFetchingFallbackAction && !isFallbackActionUninitialized) {
      dispatch(closeAndClear());
    }
  }, [
    action,
    dispatch,
    drawerProps.actionId,
    drawerProps.open,
    isFallbackActionUninitialized,
    isFetchingFallbackAction,
    isLoadingFallbackAction,
  ]);

  function onOpenClose(show: boolean) {
    if (show) {
      console.warn(`No op`);

      return;
    }

    dispatch(closeAndClear());
  }

  function ignorePenalty() {
    setShowIgnoreFeeModal(true);
  }

  async function onConfirmIgnorePenalty() {
    if (!action) return;

    await actionInvoice({
      invoiceId: action.invoiceId,
      action: `ignorePenalty`,
      organisationId: action.organisationId,
      actionId: action.id,
    });

    setShowIgnoreFeeModal(false);
  }

  function onRevert() {
    if (!action) return;

    revertAction({
      actionId: action.id,
      organisationId: action.organisationId,
    });
  }

  async function onActionNow() {
    if (!action) return;

    setShowActionNowModal(false);

    await actionNow({
      actionId: action.id,
      organisationId: action.organisationId,
    });
  }

  function onRetryAction() {
    if (!action) return;

    retryAction({
      actionId: action.id,
      organisationId: action.organisationId,
    });
  }

  function onRemoveAction() {
    if (!action) return;

    removeAction({
      actionId: action.id,
      organisationId: action.organisationId,
    });
  }

  function onSkipAction() {
    if (!action) return;

    skipActions({
      actionIds: [action.id],
      organisationId: action.organisationId,
    });
  }

  function onRemoveFromBatch() {
    if (!action) return;

    removeFromBatch({
      actionId: action.id,
      organisationId: action.organisationId,
    });
  }

  async function onResendEmail() {
    if (!action) return;

    await regenerateChildActions({
      actionId: action.id,
      organisationId: action.organisationId,
    });

    dispatch(closeAndClear());
  }

  async function onApproveAction() {
    if (!action) return;

    await approveActions({
      actionIds: [action.id],
      organisationId: action.organisationId,
      force: false,
    });

    dispatch(closeAndClear());
  }

  async function onRevertApprovals() {
    if (!action) return;

    await revertApprovals({
      actionIds: [action.id],
      organisationId: action.organisationId,
    });

    dispatch(closeAndClear());
  }

  async function downloadStatement(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, actionId: string, statementId: number, type: StatementType) {
    e.preventDefault();

    if (downloadingStatement) return;

    setDownloadingStatement(true);
    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.baseApiUrl}actions/${actionId}/statement/${statementId}/download`,
        params: {
          type,
        },
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = url;
      link.setAttribute(`download`, `historic_statement_${actionId}_${statementId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    }
    catch (e) {
      const responseObj = await e.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not download statement.`}`);
    }

    setDownloadingStatement(false);
  }

  const stats = useMemo(() => {
    if (!action) return [];

    const deferredUntil = action.deferredUntil
      ? DateTime.fromJSDate(new Date(action.deferredUntil))
      : null;
    const isDeferredUntilFuture = !!deferredUntil?.isValid
      && action.status === ActionStatus.PENDING
      && deferredUntil.toMillis() > DateTime.now().toMillis();

    const sendLike = isEmailLikeAction(action) || action.type === ActionType.SEND_SMS;

    const commonProps: DescriptionItem[] = [
      {
        title: `Scheduled for`,
        content: <DatePopover
          date={ action.actionOn }
          organisationTimezone={ action.organisationTimezone }
          labelFormat={ `ccc d LLL yyyy, HH:mm` }
        />,
      },
      ...(isDeferredUntilFuture ? [{
        title: `Deferred until`,
        content: <DatePopover
          date={ action.deferredUntil }
          organisationTimezone={ action.organisationTimezone }
          labelFormat={ `ccc d LLL yyyy, HH:mm` }
        />,
      }] : []),
      {
        title: action.executedOn ? (sendLike ? `Sent` : `Actioned`) : `Last run`,
        content: action.executedOn ?
          <DatePopover
            date={ action.executedOn }
            organisationTimezone={ action.organisationTimezone }
            labelFormat={ `ccc d LLL yyyy, HH:mm` }
          /> : <span className={ `text-gray-400 font-normal` }>{ `Not yet run` }</span>,
      },
    ];

    if (isEmailLikeAction(action)) {
      commonProps.push({
        title: `To`,
        content:
          action.payload?.contactPerson?.email ||
          action.payload?.toAddresses?.join(`, `) ||
          action.sendgridMessage?.toAddress ||
          `-`,
      });

      if (action.sendgridMessage?.fromAddress) {
        commonProps.push({
          title: `From`,
          content: action.sendgridMessage.fromAddress,
        });
      }

      if (action.sendgridMessage?.bccAddress) {
        commonProps.push({
          title: `Bcc`,
          content: action.sendgridMessage.bccAddress,
        });
      }

      if (action.sendgridMessage?.ccAddress) {
        commonProps.push({
          title: `Cc`,
          content: action.sendgridMessage.ccAddress,
        });
      }
    }

    if (action.type === ActionType.SEND_SMS) {
      commonProps.push({
        title: `To`,
        content: action.payload?.smsPayload?.to ||  `-`,
      });
    }

    if (hasOriginalInvoice.includes(action.type) && action.invoiceId) {
      commonProps.push({
        title: `Original invoice`,
        content: (<WrappedLink
          to={ `/invoices/${action.invoiceId}` }
          className={ `text-paidniceGreen-600 hover:text-paidniceGreen-700 inline-flex items-center gap-1` }
        >
          { action.invoice?.xero_number ? action.invoice?.xero_number : `No Number` }
          <ArrowTopRightOnSquareIcon
            className={ `w-3.5 h-3.5` }
            aria-hidden={ `true` }
          />
        </WrappedLink>),
      });
    }

    if (action.escalation) {
      commonProps.push({
        title: `Escalation`,
        content: (<WrappedLink
          to={ `/escalations/${action.escalation.id}/contact/${action.escalation.contactId}` }
          className={ `text-paidniceGreen-600 hover:text-paidniceGreen-700 inline-flex items-center gap-1` }
        >
          { `View escalation` }
          <ArrowTopRightOnSquareIcon
            className={ `w-3.5 h-3.5` }
            aria-hidden={ `true` }
          />
        </WrappedLink>),
      });
    }

    // Now some action specific props
    if (action.type === ActionType.LATE_FEE) {
      if (
        action.penalty?.process_type === PolicyProcessType.NEW_INVOICE &&
        action.penalty?.internal_invoice_id_for_penalty
      ) {
        commonProps.push({
          title: `New Penalty Invoice`,
          content: (<WrappedLink
            to={ `/invoices/${action.penalty?.internal_invoice_id_for_penalty}` }
            className={ `text-paidniceGreen-600 hover:text-paidniceGreen-700 inline-flex items-center gap-1` }
          >
            { action.penalty?.xero_invoice_number }
            <ArrowTopRightOnSquareIcon
              className={ `w-3.5 h-3.5` }
              aria-hidden={ `true` }
            />
          </WrappedLink>),
        });
      }

      if (action.penalty?.process_type) {
        // Legacy processed actions may not have this
        commonProps.push({
          title: `Issued As`,
          content: action.penalty?.process_type === PolicyProcessType.NEW_INVOICE ? `New Invoice` : `Line Item(s) on Original Invoice`,
        });
      }

      commonProps.push({
        title: `Fee Amount`,
        content: completeLikeActionStatuses.includes(action.status) ? formatter(toDecimal(action.penalty?.penalty_amount_total)) : `Calculated When Sent`,
      });

      if (action.status === ActionStatus.COMPLETE || action.status === ActionStatus.REVERTED) {
        commonProps.push({
          title: `Late Fee Ignored`,
          content: action.ignored ? `Yes` : (
            <Button
              onClick={ ignorePenalty }
              loading={ actionInvoiceLoading }
              color={ `red` }
            >
              { `Ignore Late Fee` }
            </Button>
          ),
          subText: `Ignoring a late fee will allow it to be recalculated & reapplied!`,
        });
      }
    }

    if (actionStatementTypes.includes(action.type)) {
      if (action.batchCount) {
        commonProps.push({
          title: `Number of actions in batch`,
          content: action.batchCount,
        });
      }
    }

    if (action.type === ActionType.ISSUE_STATEMENT_LATE_FEE && (action.status === ActionStatus.COMPLETE || action.status === ActionStatus.REVERTED)) {
      action.statementPenalties.forEach(p => {
        commonProps.push({
          title: `Interest Calculation Date`,
          content: <DatePopover
            date={ p.calculationDate }
            organisationTimezone={ action.organisationTimezone }
            labelFormat={ `ccc, DDD t` }
          />,
        });

        const title = action.statementPenalties.length > 1 ? `Issued Charges Invoice (${p.currency})` : `Issued Charges Invoice`;

        commonProps.push({
          title,
          content: (<WrappedLink
            to={ `/invoices/${p.invoiceId}` }
            className={ `text-paidniceGreen-600 hover:text-paidniceGreen-700 inline-flex items-center gap-1` }
          >
            { p.invoiceNumber }
            <ArrowTopRightOnSquareIcon
              className={ `w-3.5 h-3.5` }
              aria-hidden={ `true` }
            />
          </WrappedLink>),
        });
      });

      if (!action.statementPenalties.length) {
        commonProps.push({
          title: `Issued Charges Invoice(s)`,
          content: `No overdue balance found with policy conditions`,
        });
      }
    }

    if (action.type === ActionType.SEND_STATEMENT_INTEREST_EMAIL) {
      commonProps.push({
        title: `Currency`,
        content: action.payload?.currency || `-`,
      });
    }

    if (action.type === ActionType.SEND_STATEMENT && action.statements?.length) {
      const element = (
        <div className={ `flex flex-col` }>
          {
            action.statements.map(s => {
              const outstanding = s.storage_filename ?
                (
                  <a
                    onClick={ e => downloadStatement(e, action.id, s.id, StatementType.OUTSTANDING) }
                  >
                    <Paragraph
                      variant={ `link` }
                    >
                      { `Download ${s.currency} Statement` }
                    </Paragraph>
                  </a>
                ) : null;

              const activity = s.storage_filename_activity ?
                (
                  <a
                    onClick={ e => downloadStatement(e, action.id, s.id, StatementType.ACTIVITY) }
                  >
                    <Paragraph
                      variant={ `link` }
                    >
                      { `Download ${s.currency} Activity` }
                    </Paragraph>
                  </a>
                ) : null;

              return (
                <div
                  key={ s.id }
                >
                  { outstanding }
                  { activity }
                </div>
              );
            })
          }
        </div>
      );

      commonProps.push({
        title: `Generated Statements`,
        content: element,
      });
    }

    if (action.type === ActionType.PAYMENT_PLAN_REMINDER) {
      if (action.payload?.instalmentNumber) {
        commonProps.push({
          title: `Installment Number`,
          content: `#${action.payload.instalmentNumber}`,
        });
      }
    }

    // TODO, Ensure we save all triggering policies
    if (action.policies?.length) {
      commonProps.push({
        title: `Triggered by`,
        content: action.policies?.map(p => {
          const policy = { ...p };

          if (policy.system) {
            return `Manual Trigger`;
          }

          if (policy.deleted) {
            policy.title = `${policy.title} (Deleted)`;
          }

          return policy.title;
        }).join(`, `),
      });
    }

    // Always put the notes at the bottom
    if (action.notes.length) {
      commonProps.push({
        title: `Notes`,
        content: action.notes.map(n => actionNotesReadable[n]).join(`\n`),
      });
    }

    if (action.latestApprovalLog) {
      const isApproval = [ActionApprovalLogType.APPROVED, ActionApprovalLogType.APPROVAL_REVERTED, ActionApprovalLogType.SKIPPED].includes(action.latestApprovalLog.logType);

      commonProps.push({
        title: isApproval ? `Approval Status` : `Triggered By`,
        content: `${langauge.actionApproval.status[action.latestApprovalLog.logType]} ${isApproval && action.wasForced ? ` and run immediately` : ``} by ${action.latestApprovalLog.userEmail || `Unknown`} @ ${DateTime.fromJSDate(new Date(action.latestApprovalLog.createdAt)).toFormat(`yyyy-MM-dd HH:mm:ss ZZZZ`)}`,
      });
    }

    return commonProps;
  }, [action, downloadingStatement]);

  const smsHistory: DescriptionItem[] | null = useMemo(() => {
    if (!action) return null;
    if (action.type !== ActionType.SEND_SMS) return null;

    if ([ActionStatus.PENDING, ActionStatus.APPROVAL_PENDING].includes(action.status)) {
      return [
        {
          title: `Delivery Progress Status`,
          content: `Not yet sent`,
        },
      ];
    }

    if (action.onlyWebhook) {
      return [
        {
          title: `Delivery Progress Status`,
          content: `Not Applicable - Webhook Only`,
        },
        {
          title: `Webhook Payload To`,
          content: action.payload?.smsPayload?.to || `-`,
        },
        {
          title: `Webhook Payload Body`,
          content: action.payload?.smsPayload?.body || `-`,
        },
      ];
    }

    return [
      {
        title: `Delivery Progress Status`,
        content: action.sms?.status || `-`,
      },
      {
        title: `Sent to`,
        content: action.sms?.to || `-`,
      },
      {
        title: `Sent From`,
        content: action.sms?.from || `-`,
        subText: `The number the SMS message would have received from`,
      },
      {
        title: `Message`,
        content: action.sms?.body || `-`,
      },
    ];
  }, [action]);

  const webhookHistory: DescriptionItem[] | null = useMemo(() => {
    if (!action) return null;
    if (!action.outboundWebhooks?.length) return null;

    return action.outboundWebhooks.map(w => {
      return {
        title: new Date(w.createdAt).toLocaleString(),
        content: (
          <span className={ `flex flex-col` }>
            <span>
              { `Status: ${w.responseCode}` }
            </span>
            <span>
              { `URL: ${w.destinationUrl}` }
            </span>
          </span>
        ),
      };
    });
  }, [action]);

  // Max size of 4
  const buttons = useMemo(() => {
    if (!action) return [];

    const buttonArray: {
      label: string;
      onClick: () => void;
      disabled?: boolean;
      loading?: boolean;
      tooltip?: string;
      color?: ButtonColorOpts;
    }[] = [];

    if (action.status === ActionStatus.APPROVAL_PENDING) {
      buttonArray.push({
        label: `Approve`,
        tooltip: `Approve this action to run at the scheduled time as long as the policy conditions are still met`,
        onClick: onApproveAction,
        loading: approveActionsLoading,
      });
    }

    if (action.status === ActionStatus.PENDING && action.policies.some(p => p.state === PolicyState.APPROVAL && !p.system)) {
      buttonArray.push({
        label: `Revert approvals`,
        tooltip: `Revert the approvals for this action`,
        onClick: onRevertApprovals,
        loading: revertApprovalsLoading,
        color: `white`,
      });
    }

    if ([ActionStatus.PENDING, ActionStatus.APPROVAL_PENDING].includes(action.status) && actionConfig[action.type].canBeSkipped) {
      buttonArray.push({
        label: `Skip action`,
        onClick: onSkipAction,
        loading: skipActionsLoading,
        tooltip: `Skip this action. It will not be processed.`,
        color: `ghost`,
      });
    }

    if ([ActionStatus.PENDING, ActionStatus.APPROVAL_PENDING].includes(action.status) && actionConfig[action.type].canActionNow) {
      buttonArray.push({
        label: `Run now`,
        onClick: () => setShowActionNowModal(true),
        loading: actionNowLoading,
        tooltip: `This will process the action immediately. Ignoring the scheduled date!`,
        color: `white`,
      });
    }

    if (
      action.type === ActionType.LATE_FEE &&
      action.status === ActionStatus.COMPLETE
    ) {
      const tooltipText = action.penalty?.process_type === PolicyProcessType.NEW_INVOICE ?
        `This will attempt to void the invoice` :
        `This will attempt to remove the line item(s) from the original invoice. If there are payments applied. A credit note will be applied instead`;

      buttonArray.push({
        label: `Remove Late Fee`,
        onClick: onRevert,
        loading: revertActionLoading,
        tooltip: tooltipText,
      });

      // Also add a button to resend the late fee email
      let emailTooltip = `Resend the late fee email to contacts`;
      let emailDisabled = false;

      if (action.policies?.length) {
        const hasTemplate = action.policies.some(p => p.template_id);
        if (!hasTemplate) {
          emailTooltip = `No related policies have a late fee email template set`;
          emailDisabled = true;
        }

        const onlySendToSelf = action.policies.every(p => p.do_not_send_email_to_customer);

        if (onlySendToSelf) {
          emailTooltip = `A related policy has is configured to only send emails to the reply to address`;
          emailDisabled = true;
        }

        buttonArray.push({
          label: `Resend Late Fee Email(s)`,
          disabled: emailDisabled,
          tooltip: emailTooltip,
          onClick: onResendEmail,
          loading: regenerateChildActionsLoading,
        });
      }
    }

    if (
      action.type === ActionType.DISCOUNT_REMOVE &&
      action.status === ActionStatus.COMPLETE
    ) {

      buttonArray.push({
        label: `Re-apply discount`,
        onClick: onRevert,
        loading: revertActionLoading,
        tooltip: `Attempt to re-apply the discount`,
      });
    }

    if (
      action.type === ActionType.ISSUE_STATEMENT_LATE_FEE &&
      action.status === ActionStatus.COMPLETE &&
      action.statementPenalties?.length
    ) {
      const tooltipText = `This will attempt to void the invoice. Excluding this from future calculations`;

      buttonArray.push({
        label: `Remove Interest Charge(s)`,
        onClick: onRevert,
        loading: revertActionLoading,
        tooltip: tooltipText,
      });
    }

    if (action.type === ActionType.DISCOUNT_REMOVE) {
      if (action.status === ActionStatus.PENDING) {
        buttonArray.push({
          label: `Remove Now`,
          onClick: () => actionInvoice({
            invoiceId: action.invoiceId,
            action: `actionNow`,
            organisationId: action.organisationId,
            actionId: action.id,
          }),
        });
      }
    }

    if (actionStatementTypes.includes(action.type) && action.batchCount && completeLikeActionStatuses.includes(action.status)) {
      buttonArray.push({
        label: `Remove From Batch`,
        tooltip: `Removing from batch allows other pending actions to be cleared`,
        onClick: onRemoveFromBatch,
        loading: removeFromBatchLoading,
      });
    }

    if (action.status === ActionStatus.FAILED && actionConfig[action.type].canRetryOnFail && !action.irreversibleStepStarted) {
      buttonArray.push({
        label: `Retry`,
        onClick: onRetryAction,
        loading: retryActionLoading,
        tooltip: `Run this action again now using its saved details`,
      });
    }

    if (
      (action.status === ActionStatus.FAILED && actionConfig[action.type].canRemoveOnFail)
      // Also allow removing a interest charge that was a noop
      || (action.status === ActionStatus.COMPLETE && action.type === ActionType.ISSUE_STATEMENT_LATE_FEE && !action.statementPenalties?.length)
    ) {
      buttonArray.push({
        label: `Delete action`,
        onClick: onRemoveAction,
        loading: removeActionLoading,
        color: `red`,
        tooltip: `Delete action to allow it to be retried. (If policy conditions are still met)`,
      });
    }
    else if (action.status === ActionStatus.SKIPPED) {
      buttonArray.push({
        label: `Delete action`,
        onClick: onRemoveAction,
        loading: removeActionLoading,
        color: `red`,
        tooltip: `Delete action to allow it to be recreated and remove skip. (If policy conditions are still met)`,
      });
    }

    if ([ActionStatus.PENDING, ActionStatus.APPROVAL_PENDING].includes(action.status) && actionConfig[action.type].canRemovePending) {
      buttonArray.push({
        label: `Cancel action`,
        onClick: onRemoveAction,
        loading: removeActionLoading,
        color: `ghost`,
        tooltip: `Cancel this action`,
      });
    }

    return buttonArray;
  }, [action, actionInvoiceLoading, ignorePenalty, onRevert, revertActionLoading, contactGroup, regenerateChildActionsLoading, skipActionsLoading, removeActionLoading, approveActionsLoading, revertApprovalsLoading, actionNowLoading, retryActionLoading, removeFromBatchLoading]);

  const TypeIcon = (action && typeIcons[action.type]) || (action && isEmailLikeAction(action) ? EnvelopeIcon : BoltIcon);

  const recipient = action && (
    action.type === ActionType.SEND_SMS
      ? action.payload?.smsPayload?.to
      : isEmailLikeAction(action)
        ? (action.payload?.contactPerson?.email || action.payload?.toAddresses?.join(`, `) || action.sendgridMessage?.toAddress)
        : action.contact?.name
  );

  const emailEvents = action && isEmailLikeAction(action) ? action.sendgridMessage?.events : undefined;

  return (
    <Drawer
      open={ drawerProps.open }
      setOpen={ onOpenClose }
      size={ `lg` }
      bare
    >
      <When condition={ !action }>
        <div className={ `bg-paidniceGreen-50 border-b border-paidniceGreen-100 px-6 pt-6 pb-5 flex items-start justify-between` }>
          <Paragraph variant={ `secondary` }>{ `Loading action...` }</Paragraph>
          <button
            type={ `button` }
            className={ `-mt-1 -mr-2 p-1.5 rounded-md text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-paidniceGreen-500` }
            onClick={ () => onOpenClose(false) }
          >
            <span className={ `sr-only` }>{ `Close panel` }</span>
            <XMarkIcon className={ `w-5 h-5` }
              aria-hidden={ `true` } />
          </button>
        </div>
      </When>

      <When condition={ !!action }>
        { () => (
          <>
            { /* Tinted summary header */ }
            <div className={ `bg-paidniceGreen-50 border-b border-paidniceGreen-100 px-6 pt-6 pb-5` }>
              <div className={ `flex items-start gap-3` }>
                <div className={ `w-10 h-10 rounded-lg bg-white border border-paidniceGreen-100 flex items-center justify-center shrink-0` }>
                  <TypeIcon className={ `w-5 h-5 text-paidniceGreen-600` } />
                </div>
                <div className={ `flex-1 min-w-0` }>
                  <div className={ `text-[11px] font-semibold tracking-[0.09em] uppercase text-paidniceGreen-500` }>
                    { eyebrow[action.status] }
                  </div>
                  <div className={ `flex items-center gap-2.5 mt-0.5 flex-wrap` }>
                    <span className={ `text-lg font-semibold text-paidniceGreen-900 tracking-tight` }>
                      { langauge.action[action.type].label }
                    </span>
                    <ActionStatusBadge action={ action }
                      size={ `md` } />
                    <When condition={ action.onlyWebhook }>
                      <Tooltip title={ `Did not send to contact. Only triggered webhook` }>
                        <Badge color={ `orange` }
                          size={ `sm` }>
                          { `Webhook Only` }
                        </Badge>
                      </Tooltip>
                    </When>
                  </div>
                  <div className={ `text-[13px] text-gray-600 mt-1 flex items-center gap-1 flex-wrap` }>
                    <DatePopover
                      date={ action.executedOn || action.actionOn }
                      organisationTimezone={ action.organisationTimezone }
                      labelFormat={ `ccc d LLL yyyy, HH:mm` }
                    />
                    <When condition={ !!recipient }>
                      { `· ${isEmailLikeAction(action) || action.type === ActionType.SEND_SMS ? `to ` : ``}${recipient}` }
                    </When>
                  </div>
                  <Paragraph variant={ `help` }
                    className={ `mt-1` }>
                    { langauge.action[action.type].userDescription }
                  </Paragraph>
                </div>
                <button
                  type={ `button` }
                  className={ `-mt-1 -mr-2 p-1.5 rounded-md text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-paidniceGreen-500` }
                  onClick={ () => onOpenClose(false) }
                >
                  <span className={ `sr-only` }>{ `Close panel` }</span>
                  <XMarkIcon className={ `w-5 h-5` }
                    aria-hidden={ `true` } />
                </button>
              </div>

              <When condition={ !!buttons.length }>
                <div className={ `flex flex-wrap gap-2 mt-4` }>
                  {
                    buttons.map((button, i) => (
                      <Button
                        key={ i + `action-button` }
                        onClick={ button.onClick }
                        disabled={ button.disabled }
                        loading={ button.loading }
                        tooltip={ button.tooltip }
                        color={ button.color || `white` }
                      >
                        { button.label }
                      </Button>
                    ))
                  }
                </div>
                <When condition={ !!buttons.find(b => b.label === `Remove Late Fee`) }>
                  <Paragraph variant={ `help` }
                    className={ `mt-2` }>
                    {
                      action.penalty?.process_type === PolicyProcessType.NEW_INVOICE ?
                        `Removing this late fee will attempt to void the created invoice` :
                        `Removing this late fee will attempt to remove the late line item(s) added to the original invoice. If there are any payments/credit notes applied. A credit note will be applied instead.`
                    }
                  </Paragraph>
                </When>
                <When condition={ action.status === ActionStatus.SKIPPED }>
                  <Paragraph variant={ `help` }
                    className={ `mt-2` }>
                    { `To unskip an action it must be deleted. If the policy conditions are still met it will be recreated as a pending action.` }
                  </Paragraph>
                </When>
              </When>
            </div>

            <When condition={ action.createdFromPreview }>
              <Notification type={ `info` }
                className={ `mx-6 mt-4` }>
                { `Created manually from policy preview. Some checks were omitted.` }
              </Notification>
            </When>

            { /* Details grid */ }
            <div className={ `px-6 py-5 border-b border-gray-100` }>
              <DetailGrid items={ stats } />
            </div>

            <When condition={ !!emailEvents?.length }>
              { () => (
                <div className={ `px-6 py-5 border-b border-gray-100` }>
                  <SectionLabel>{ `Delivery history` }</SectionLabel>
                  <DeliveryTimeline
                    events={ emailEvents }
                    timezone={ action.organisationTimezone }
                    retrying={ action.subStatus === ActionSubStatus.DEFERRED }
                  />
                </div>
              ) }
            </When>

            <When condition={ !!smsHistory }>
              <div className={ `px-6 py-5 border-b border-gray-100` }>
                <SectionLabel>{ `SMS delivery` }</SectionLabel>
                <DetailGrid items={ smsHistory } />
              </div>
            </When>

            <When condition={ !!webhookHistory }>
              <div className={ `px-6 py-5 border-b border-gray-100` }>
                <SectionLabel>{ `Webhooks` }</SectionLabel>
                <DetailGrid items={ webhookHistory } />
              </div>
            </When>

            <When condition={ !!action.emailCommunication }>
              { () => (
                <div className={ `px-6 py-5` }>
                  <SectionLabel>{ `Email` }</SectionLabel>
                  <EmailCard
                    subject={ action.emailCommunication.emailContent?.subject }
                    from={ action.emailCommunication.from }
                    replyTo={ action.emailCommunication.replyTo }
                    to={ action.emailCommunication.to }
                    cc={ action.emailCommunication.cc }
                    bcc={ action.emailCommunication.bcc }
                    alsoSentTo={ action.emailCommunication.alsoSentTo }
                    attachments={ action.emailCommunication.emailContent?.attachments }
                    html={ action.emailCommunication.emailContent?.htmlContent }
                  />
                </div>
              ) }
            </When>

            <div className={ `px-6` }>
              <ActionPreviewSection action={ action } />
            </div>

            <When condition={ action.errorMessage }>
              <div className={ `px-6 py-5` }>
                <SectionLabel>{ `Errors` }</SectionLabel>
                <Paragraph variant={ `error` }>
                  { action.errorMessage }
                </Paragraph>
              </div>
            </When>
          </>
        ) }
      </When>

      <ActionNowModal
        message={ `This will process the action immediately, even in safe mode! Regardless of the scheduled date!` }
        onCancel={ () => setShowActionNowModal(false) }
        onConfirm={ onActionNow }
        show={ showActionNowModal }
      />

      <IgnoreLateFeeModal
        message={ `Ignoring the late fee will allow it to be recalculated and reapplied if its still applicable. Do not do this if you no longer want a late fee applied to this invoice!` }
        onCancel={ () => setShowIgnoreFeeModal(false) }
        onConfirm={ onConfirmIgnorePenalty }
        show={ showIgnoreFeeModal }
      />
    </Drawer>
  );
}
